// The property a page's data was loaded for, named back when the page writes.
//
// The open property is server-side state that every tab of a user shares, and
// a page reads it once, when it loads. A write to "the open property" names the
// property its data was loaded for in the `connectionId` query parameter, and
// the service refuses it with `409 ACTIVE_CONNECTION_CHANGED` once another
// property is open (`optivo-service/src/routes/loadedConnection.ts`). The name
// never goes in the body: a credential save stores the body's other fields as
// the provider config, and a service released before the rule would have
// stored it there.

import { ACTIVE_CONNECTION_CHANGED, ActiveConnectionChangedError } from "./apiClient";
import { requestOpenPropertyCheck } from "./openPropertyEvents";

/**
 * The property a page's data was loaded for, as the read's answer named it:
 * its id, or null for data loaded with no property open. Undefined names
 * nothing — the answer did not say (a service released before the field, or a
 * read that failed), or the data is a new property's, which belongs to none.
 */
export type LoadedConnectionId = number | null | undefined;

/**
 * The query naming a page's property. Nothing for a property the page does not
 * know: `none` would be refused once a property is open, which a page loaded
 * from a service that never named one could not have meant.
 */
export function loadedConnectionQuery(loaded: LoadedConnectionId): string {
    if (loaded === undefined) return "";
    return `?connectionId=${encodeURIComponent(loaded === null ? "none" : String(loaded))}`;
}

/**
 * The property the pages on screen were loaded for, as the shell last said.
 *
 * A page whose own reads name their property keeps that id and names it back
 * (`loadedConnectionQuery`). The Prices page's reads name nothing — a dozen of
 * them, two grids apart — so its changes name the property the shell loaded
 * the page for instead: the one the sidebar names, which is the one the
 * operator believes they are changing. The shell pins it to the page's mount
 * (`App.tsx`), so a page still on screen while a switch made in this tab
 * settles keeps naming its own property rather than the one being opened.
 *
 * Undefined outside the shell, before `/api/me` has answered, and with no
 * property open, and a request then names nothing: `none` would be refused
 * the moment the wizard's PMS step opened a property in this very tab.
 */
let pageConnectionId: number | undefined;

/** Said by the shell whenever the property the page on screen was loaded for changes. */
export function setPageConnection(connectionId: number | undefined): void {
    pageConnectionId = connectionId;
}

/**
 * `path` naming the property the page on screen was loaded for, so the service
 * refuses the request once another one is open — `path` itself while that is
 * not known.
 */
export function withPageConnection(path: string): string {
    if (pageConnectionId === undefined) return path;
    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}connectionId=${encodeURIComponent(String(pageConnectionId))}`;
}

/**
 * Whether a refusal's body is the service saying the request named a property
 * that is no longer open.
 */
export function isActiveConnectionChangedRefusal(data: unknown): boolean {
    return typeof data === "object" && data !== null
        && (data as { code?: unknown }).code === ACTIVE_CONNECTION_CHANGED;
}

/**
 * The refusal as a page meets it: {@link ActiveConnectionChangedError}. The
 * shell is asked in the same breath which property is open, because a page
 * refused for another one is a tab that may be showing a property no longer
 * open (`openPropertyEvents.ts`) — and the notice it then shows in place of the
 * page is what says so, whichever page made the request.
 */
export function activeConnectionChangedError(message: string): ActiveConnectionChangedError {
    requestOpenPropertyCheck();
    return new ActiveConnectionChangedError(message);
}

/**
 * What a refused write throws: {@link ActiveConnectionChangedError} when the
 * page was loaded for another property than the one open now, otherwise an
 * error carrying the service's own reason.
 */
export async function refusedWriteError(res: Response, fallback: string): Promise<Error> {
    const data = await res.json().catch(() => ({}));
    const message: string = data.error || `${fallback}: ${res.status}`;
    return isActiveConnectionChangedRefusal(data)
        ? activeConnectionChangedError(message)
        : new Error(message);
}
